import { memo } from "react";
import { Button, Col } from "react-bootstrap";

import {
    MonthTimeRange,
    ReportFilterItem,
    ReportFilterItemsState,
    ReportItemMap,
    ReportTimeRangeType,
} from "../../types/Reports";
import ReportMonthPicker from "./ReportMonthPicker";
import ReportTimeTypePicker from "./ReportTimeTypePicker";
import ReportDropdown from "./ReportDropdown";
import { REPORT_SENSOR_REGISTRY } from "constant/report";

type ReportFiltersProps = {
    items: ReportFilterItemsState;
    handleItemChanged: (key: keyof ReportItemMap, newList: ReportFilterItem[]) => void;

    timeRange: MonthTimeRange;
    handleTimeRangeChange: (value: MonthTimeRange) => void;

    timeRangeType: ReportTimeRangeType;
    handleTimeRangeTypeChange: (value: ReportTimeRangeType) => void;

    isDisableApply: boolean;
    handleApply: () => Promise<void>;
};

const DROPDOWN_INFO = {
    room: {
        title: 'Rooms',
        placeholder: 'rooms',
        limitText: 'rooms',
    },
    energy: {
        title: 'Energy Meters',
        placeholder: 'meters',
        limitText: 'energy meters',
    },
    water: {
        title: 'Water Flow Meters',
        placeholder: 'meters',
        limitText: 'water flow meters',
    },
    waterLeakage: {
        title: 'Water Leakage',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    temperature: {
        title: 'Temperature',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    humidity: {
        title: 'Humidity',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    co2: {
        title: 'CO2',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    pressure: {
        title: 'Pressure',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    tvoc: {
        title: 'TVOC',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    pm1: {
        title: 'PM1.0',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    'pm2.5': {
        title: 'PM2.5',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
    pm10: {
        title: 'PM10',
        placeholder: 'sensors',
        limitText: 'sensors',
    },
};

export default memo(function ReportFilters({
    items,
    handleItemChanged,

    timeRange,
    handleTimeRangeChange,

    timeRangeType,
    handleTimeRangeTypeChange,

    isDisableApply,
    handleApply,
}: Readonly<ReportFiltersProps>) {

    const onChange = (type: keyof ReportItemMap) => (newList: ReportFilterItem[]) => {
        handleItemChanged(type, newList);
    };

    const onClickApply = () => {
        handleApply().catch((error) => {
            console.error('Failed to apply new filters', error);
        });
    };

    return <Col sm={12} className="report-filter">
        <p className="report-filter-resources">Resources</p>
        <section className="d-flex flex-row report-filter-container report-filter-gap">
            {(Object.keys(items) as (keyof ReportFilterItemsState)[]).map((type) => (
                <ReportDropdown
                    key={`report-filter-dropdown-${type}`}
                    title={DROPDOWN_INFO[type].title}
                    items={items[type].all}
                    selectedItems={items[type].selected}
                    searchTextPlaceholder={`Search for ${DROPDOWN_INFO[type].placeholder}`}
                    limit={REPORT_SENSOR_REGISTRY[type].filterMax}
                    limitText={DROPDOWN_INFO[type].limitText}
                    emptyText={`No ${REPORT_SENSOR_REGISTRY[type].filterEmptySubject} to display`}
                    onChange={onChange(type)}
                />
            ))}
        </section>
        <section className="d-flex flex-row justify-content-between report-filter-container report-filter-day-type-gap">
            <div className="d-flex flex-row report-filter-range-type">
                <ReportMonthPicker
                    timeRange={timeRange}
                    handleTimeRangeChange={handleTimeRangeChange}
                />
                <ReportTimeTypePicker
                    timeRangeType={timeRangeType}
                    handleTimeRangeTypeChange={handleTimeRangeTypeChange}
                />
            </div>
            <div className="d-flex flex-row report-filter-action">
                <Button
                    variant="primary"
                    className="report-filter-btn-apply"
                    onClick={onClickApply}
                    disabled={isDisableApply}
                >
                    Apply
                </Button>
            </div>
        </section>
    </Col>;
});
